import React, { useState, useRef, useEffect } from 'react';
import "../../Styles/Mail/EmailTagInput.scss"

const EmailTagInput = ({
  name,
  value,
  onChange,
  placeholder,
  label,
  required = false,
  className = "",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [emails, setEmails] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const editInputRef = useRef(null);
  const isMountedRef = useRef(true);
  
  const lastValueRef = useRef(value);
  const isInternalUpdateRef = useRef(false);

  // Fonction d'extraction des emails d'une chaîne de caractères
  const extractEmails = (inputString) => {
    const emailRegex = /[\w.-]+@[\w-]+(?:\.[\w-]{2,})+/g;
    const emails = inputString.match(emailRegex);
    return emails ? emails : [];
  };

  // Validation d'email simple
  const isValidEmail = (email) => {
    const emailRegex = /^[\w.-]+@[\w-]+(?:\.[\w-]{2,})+$/;
    return emailRegex.test(email.trim());
  };

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialisation des emails depuis la prop value - CORRIGÉ
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      
      if (value) {
        const extractedEmails = extractEmails(value);
        const validEmails = extractedEmails.filter(email => isValidEmail(email.trim()));
        setEmails(validEmails);
      } else {
        setEmails([]);
      }
    }
  }, [value]);

  // Mise à jour du parent quand les emails changent - CORRIGÉ
  useEffect(() => {
    const emailString = emails.join(', ');
    
    if (emailString !== value && !isInternalUpdateRef.current) {
      isInternalUpdateRef.current = true;
      lastValueRef.current = emailString;
      
      onChange({
        target: {
          name,
          value: emailString
        }
      });
    }
  }, [emails, name, onChange]);

  // Focus sur l'input d'édition quand on commence à éditer
  useEffect(() => {
    if (editingIndex !== -1 && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  // Gestion de la saisie
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Gestion des touches spéciales
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails.length - 1);
    }
  };

  // Gestion du collage
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const extractedEmails = extractEmails(pastedText);

    if (extractedEmails.length > 0) {
      const validEmails = extractedEmails
        .map(email => email.trim())
        .filter(email => isValidEmail(email))
        .filter((email, index, array) => array.indexOf(email) === index)
        .filter(email => !emails.includes(email));

      if (validEmails.length > 0) {
        setEmails(prev => [...prev, ...validEmails]);
        setInputValue('');
      }
    } else {
      setInputValue(prev => prev + pastedText);
    }
  };

  // Gestion de la perte de focus
  const handleBlur = (e) => {
    if (containerRef.current && e.relatedTarget && containerRef.current.contains(e.relatedTarget)) {
      return;
    }

    setIsInputFocused(false);
    if (inputValue.trim()) {
      addEmail();
    }
  };

  // Ajouter un email
  const addEmail = () => {
    const email = inputValue.trim();
    if (email && isValidEmail(email) && !emails.includes(email)) {
      setEmails(prev => [...prev, email]);
      setInputValue('');
    } else if (email) {
      setInputValue('');
    }
  };

  // Supprimer un email
  const removeEmail = (index) => {
    setEmails(prev => prev.filter((_, i) => i !== index));
    setEditingIndex(-1);
  };

  // Commencer l'édition d'un email
  const startEditing = (index) => {
    if (disabled) return;
    setEditingIndex(index);
    setEditingValue(emails[index]);
  };

  // Annuler l'édition
  const cancelEditing = () => {
    setEditingIndex(-1);
    setEditingValue('');
  };

  // Sauvegarder l'édition
  const saveEdit = () => {
    const trimmedValue = editingValue.trim();
    if (trimmedValue && isValidEmail(trimmedValue)) {
      const emailExists = emails.some((email, index) =>
        email === trimmedValue && index !== editingIndex
      );

      if (!emailExists) {
        setEmails(prev => prev.map((email, index) =>
          index === editingIndex ? trimmedValue : email
        ));
      }
    }
    cancelEditing();
  };

  // Gestion des touches pour l'édition
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  // Focus sur le conteneur
  const handleContainerClick = (e) => {
    if (e.target.closest('.email-tag-input__tag--editing') ||
      e.target.closest('.email-tag-input__tag-remove') ||
      e.target.closest('.email-tag-input__tag-text')) {
      return;
    }

    if (inputRef.current && !disabled) {
      inputRef.current.focus();
      setIsInputFocused(true);
    }
  };

  return (
    <div className="email-tag-input__field">
      <label className="email-tag-input__label">
        {label}
        {required && <span className="email-tag-input__required">*</span>}
      </label>

      <div
        ref={containerRef}
        className={`email-tag-input__container ${isInputFocused ? 'email-tag-input__container--focused' : ''} ${disabled ? 'email-tag-input__container--disabled' : ''} ${className}`}
        onClick={handleContainerClick}
      >
        <div className="email-tag-input__tags">
          {emails.map((email, index) => (
            <div key={`${email}-${index}`} className={`email-tag-input__tag ${editingIndex === index ? 'email-tag-input__tag--editing' : ''}`}>
              {editingIndex === index ? (
                <input
                  ref={editInputRef}
                  type="email"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  onBlur={saveEdit}
                  className="email-tag-input__edit-input"
                  autoFocus
                />
              ) : (
                <>
                  <span
                    className="email-tag-input__tag-text"
                    onClick={() => startEditing(index)}
                    title={`Cliquez pour modifier: ${email}`}
                  >
                    {email}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      className="email-tag-input__tag-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEmail(index);
                      }}
                      aria-label={`Supprimer ${email}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <input
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setIsInputFocused(true)}
          onBlur={handleBlur}
          placeholder={emails.length === 0 ? placeholder : ''}
          className="email-tag-input__input"
          disabled={disabled}
          autoComplete="off"
        />
      </div>

      {name === "to" && <span className="email-tag-input__hint">
        Cliquez sur un email pour le modifier, utilisez Entrée/virgule pour ajouter
      </span>}
    </div>
  );
};

export default EmailTagInput;